import DataLoader from 'dataloader'
import { NotFound } from 'fejl'
import { orderBy } from 'lodash'
import memoize from 'memoizee'
import mime from 'mime'
import { Observable, race, firstValueFrom } from 'rxjs'
import { first, map, switchMap } from 'rxjs/operators'
import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import User from 'iam/models/User'
import { GetCurrentMember } from 'iam/SessionStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { Env } from 'infra/env'
import { DocumentDownloadDelegate } from 'shell/platform/types'
import { GetToken, Id, PrimeProps } from 'types/definitions'
import InfiniteScrollProvider, {
  FetchItems,
} from 'utils/InfiniteScrollProvider'
import { getCacheKeyForObject } from 'utils/object-util'
import { DocumentsAPI } from './API'
import Document from './Document'
import {
  GetAccessesForDocument,
  PrimeDocumentAccess,
} from './DocumentAccessStore'
import { GetDocumentTag, PrimeDocumentTag } from './DocumentTagStore'
import { PrimeDocumentType } from './DocumentTypeStore'
import DocumentVersion, { DocumentVersionFile } from './DocumentVersion'
import {
  GetVersionsForDocument,
  PrimeDocumentVersion,
} from './DocumentVersionStore'
import {
  CreateDocumentPayload,
  DocumentDTO,
  FindDocumentsQuery,
  JobDocumentsQuery,
  UpdateDocumentPayload,
  UserDocumentsQuery,
  DocumentPopulate,
  ZipFilesPayload,
  AssignDocumentTypePayload,
  SearchDocumentsQuery,
} from './dtos/document'
import {
  DocumentCreatedMessage,
  DocumentUpdatedMessage,
  ZipCreatedMessage,
  ZipFailedMessage,
} from './dtos/realtime-messages'

export type GetDocument = (id: Id) => Document | undefined

export type PrimeDocument = (
  props: PrimeProps<DocumentDTO, 'long_id'>,
) => Document | undefined

const orderDocuments = (docs: Array<Document>) =>
  orderBy(docs, 'dateCreated', 'desc')

export default class DocumentStore {
  documents = collection<DocumentDTO, Document>({
    getDataId: (data) => data.long_id,
    getModelId: (model) => model.longId,
    create: () =>
      new Document(
        this.primeMember,
        this.getCurrentMember,
        this.primeDocumentTag,
        this.primeDocumentAccess,
        this.primeDocumentVersion,
        this.primeDocumentType,
        this.getDocumentTag,
        this.getAccessesForDocument,
        this.getVersionsForDocument,
        this.primeWorkspace,
      ),
  })

  /**
   * Scroll providers.
   */
  scrollProviders = new Map<
    string,
    InfiniteScrollProvider<Document, Record<string, unknown>>
  >()

  /**
   * Documents loader.
   */
  documentsLoader = new DataLoader(this._fetchDocumentsByIds.bind(this), {
    cacheKeyFn: getCacheKeyForObject,
  })

  constructor(
    private api: DocumentsAPI,
    private primeMember: PrimeMember,
    private getCurrentMember: GetCurrentMember,
    private getToken: GetToken,
    private primeDocumentTag: PrimeDocumentTag,
    private primeDocumentAccess: PrimeDocumentAccess,
    private primeDocumentVersion: PrimeDocumentVersion,
    private primeDocumentType: PrimeDocumentType,
    private getDocumentTag: GetDocumentTag,
    private getAccessesForDocument: GetAccessesForDocument,
    private getVersionsForDocument: GetVersionsForDocument,
    private primeWorkspace: PrimeWorkspace,
    private getDocumentDownloadDelegate: () => DocumentDownloadDelegate,
    currentMember$: Observable<Member>,
  ) {
    this.fetchUserDocuments = this.fetchUserDocuments.bind(this)
    this.primeDocument = memoize(this.primeDocument, {
      promise: true,
      length: 1,
    })

    this.api
      .when<DocumentUpdatedMessage>('DocumentUpdated')
      .subscribe((doc) => {
        if (!doc.data.archived) {
          this.documents.set(doc.data)
        }
      })
    this.api
      .when<DocumentCreatedMessage>('DocumentCreated')
      .subscribe((doc) => this.documents.set(doc.data))

    currentMember$.subscribe(() => {
      // this.documents.clear()
      this.scrollProviders.clear()
      this.userDocumentsScrollProvider.reset(false)
    })
  }

  /**
   * Scroll provider for the current user's own documents.
   */
  get userDocumentsScrollProvider() {
    return this.getScrollProvider('user', this.fetchUserDocuments)
  }

  /**
   * Scroll provider for the workspace member in the specified workspace.
   * @param workspaceId
   */
  userDocumentsScrollProviderForWorkspace(workspaceId: Id) {
    workspaceId = Id.assert(workspaceId)
    return this.getScrollProvider(`workspace-${workspaceId}`, (params) =>
      this.fetchUserDocuments({
        ...params,
        workspace_id: workspaceId,
      }),
    )
  }

  /**
   * Gets or inits a scroll provider.
   * A scroll provider is used to manage infinite scrolling state
   * for APIs using cursor-based paging.
   */
  getScrollProvider(uid: string, fetchItems: FetchItems<Document>) {
    const existing = this.scrollProviders.get(uid)
    if (existing) {
      return existing
    }

    const newProvider = new InfiniteScrollProvider({
      fetchItems,
      memoizeInitial: true,
      limit: 30,
    })

    this.scrollProviders.set(uid, newProvider)
    return newProvider
  }

  forJob(jobId: string) {
    return orderDocuments(
      this.documents.filter((doc) => {
        const access = doc.accesses.find((a) => a.jobId === jobId)
        return !!access && !doc.ephemeral
      }),
    )
  }

  forMember(member: Member) {
    return orderDocuments(
      this.documents.filter((doc) => doc.owner === member && !doc.ephemeral),
    )
  }

  forUser(user: User) {
    return orderDocuments(
      this.documents.filter((doc) => doc.owner.user === user && !doc.ephemeral),
    )
  }

  primeDocument: PrimeDocument = (props) => {
    if (!props?.long_id) {
      return undefined
    }

    let doc = this.documents.get(props.long_id)

    // If we have date created and the latest version of the doc,
    // then we don't need to fetch it.
    if (doc?.dateCreated && doc.latestVersion) {
      return doc
    }

    if (!doc) {
      doc = this.documents.create(props)
    }

    // Fetch the doc in the background, including it's related information.
    this._fetchDocumentsByIds([
      [props.long_id, { accesses: true, history: true, tags: true }],
    ]).then()

    return doc
  }

  async findDocuments(params: FindDocumentsQuery) {
    return this._query(params)
  }

  async _fetchDocumentsByIds(ids: readonly [string, DocumentPopulate][]) {
    return Promise.all(
      ids.map(([longId, populate]) =>
        this.api.get(longId, { ...populate }).then(this.documents.set),
      ),
    )
  }

  async fetchDocumentsForJob(params: JobDocumentsQuery) {
    return this._query(params)
  }

  async fetchUserDocuments(params: Omit<UserDocumentsQuery, 'context'>) {
    return this._query({
      context: 'User',
      workspace_id: Id.assert(this.getCurrentMember().workspace.id),
      ...params,
    })
  }

  async fetchDocument(id: Id, populate?: DocumentPopulate) {
    return this.documentsLoader.load([Id.assert(id), { ...populate }])
  }

  async searchDocuments(params: SearchDocumentsQuery) {
    return this.api.search(params).then((paged) => ({
      ...paged,
      data: this.documents.set(paged.data),
    }))
  }

  async createDocument(data: CreateDocumentPayload): Promise<Document> {
    return this.api.create(data).then((documentDto) => {
      // create the document setting the uploadProgress prop before adding it to the collection
      // to avoid showing the file as corrupted while the version is being created
      const document = this.documents.create(documentDto).set({
        uploadProgress: {
          total: 0,
          percent: 0,
          uploaded: 0,
        },
      })

      this.documents.add(document)

      return document
    })
  }

  async updateDocument(longId: string, data: UpdateDocumentPayload) {
    const updated = await this.api.update(longId, data)
    return this.documents.set(updated)
  }

  async archiveDocument(longId: string) {
    const archived = await this.api.archive(longId)
    return this.documents.remove(archived.long_id)
  }

  /**
   * Archives a document, but doesn't remove it from the store.
   */
  async adminArchiveDocument(longId: string) {
    await this.api.archive(longId)
  }

  async assignType(id: string, data: AssignDocumentTypePayload) {
    const updated = await this.api.assignType(id, data)
    return this.documents.set(updated)
  }

  async downloadAll(docs: ZipFilesPayload) {
    const ephemeralDto = await this.api.zipFiles(docs)

    // Wait for the success message
    const created$ = this.api
      .when<ZipCreatedMessage>('ZipCreated')
      .pipe(first((msg) => msg.data.document_id === ephemeralDto.id))
    // Throw when we get an error
    const failed$ = this.api.when<ZipFailedMessage>('ZipFailed').pipe(
      first((msg) => msg.data.document_id === ephemeralDto.id),
      map((msg) => {
        throw new NotFound(msg.data.reason)
      }),
    )
    return firstValueFrom(
      race(created$, failed$).pipe(
        switchMap(() =>
          this.fetchDocument(ephemeralDto.long_id, {
            accesses: true,
            tags: true,
            history: true,
          }),
        ),
        switchMap(async (doc) => this.download(doc, false)),
      ),
    )
  }

  async downloadBlob(
    document: Document,
    version?: DocumentVersion,
    file?: DocumentVersionFile,
  ) {
    version = version || document.latestVersion
    if (!version) {
      return
    }

    file = file || (version && version.files[0])
    if (!file) {
      return
    }

    const blob = await this.api
      .getDownloadUrl(Id.assert(document.longId))
      .then((response) => fetch(response.url))
      .then((response) => response.blob())

    return new Blob([blob], { type: mime.getType(file.fileName) || blob.type })
  }

  download(
    document: Document,
    inline: boolean,
    version?: DocumentVersion,
    file?: DocumentVersionFile,
  ) {
    version = version || document.latestVersion
    if (!version) {
      return
    }
    file = file || (version && version.files[0])
    if (!file) {
      return
    }
    const token = this.getToken()
    if (!token) {
      throw new Error('Unable to download document as there is no token.')
    }

    const downloadDelegate = this.getDocumentDownloadDelegate()
    if (
      !downloadDelegate.requiresFormSubmission &&
      isDefaultDocumentDownload(document, version, file)
    ) {
      return this.api
        .getDownloadUrl(Id.assert(document.longId))
        .then((response) => downloadDelegate.download(response.url))
    }

    triggerDownload(document, version, file, inline, token)
  }

  async _query(query: FindDocumentsQuery) {
    const paged = await this.api.find({
      ...query,
      accesses: true,
      history: true,
      tags: true,
    })
    return { ...paged, data: this.documents.set(paged.data) }
  }
}

function isDefaultDocumentDownload(
  document: Document,
  version: DocumentVersion,
  file: DocumentVersionFile,
) {
  const latestVersion = document.latestVersion
  const defaultFile = version.files[0]

  return latestVersion?.id === version.id && defaultFile?.id === file.id
}

function triggerDownload(
  document: Document,
  version: DocumentVersion,
  file: DocumentVersionFile,
  inline: boolean,
  token: string,
) {
  // The trick is to submit a POST form to the download
  // endpoint, that way the browser won't block it as a popup and we
  // can safely transfer the auth token.
  const url = `${Env.DOCUMENT_API_EXTERNAL}/v3/documents/${document.longId}/versions/${version.id}/download/${file.id}?preview=${inline}`
  const form = window.document.createElement('form')
  form.action = url
  form.method = 'post'
  form.target = '_blank'
  const input = window.document.createElement('input')
  input.type = 'hidden'
  input.name = '__authorization'
  input.value = `Bearer ${token}`
  form.appendChild(input)
  window.document.body.appendChild(form)
  form.submit()
  window.document.body.removeChild(form)
}
